import React, { useContext, useEffect, useState } from 'react';
import { useLoading } from '../../utils/LoadingUtil';
import productGroupModel from '../../model/product/ProductGroup.model';
import ProductGroupContext from './ProductGroupContext';
import axiosInstance from '../../utils/axiosInstance';
import { ConfirmAlert, showAlert } from '../../components';
import validateForm from '../../utils/validateForm';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '@/lib/handleApiError';

const ProductGroupContextProvider = ({ children }) => {
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { productGroup, setProductGroup, initialState } = productGroupModel();
  const [error, setError] = useState(null);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [productGroupResponse, setProductGroupResponse] = useState([]);
  const [salesDepartmentResponse, setSalesDepartmentResponse] = useState([]);
  const [mainProductGroupResponse, setMainProductGroupResponse] = useState([]);
  const [powerBiMainProductGroupResponse, setPowerBiMainProductGroupResponse] =
    useState([]);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [response, setResponse] = useState([]);

  const nav = useNavigate();

  useEffect(() => {
    setCurrentPage(0);
    setSearchQuery('');
    setPageSize(100);
    setSortBy('');
    setSortDir('desc');
  }, [nav]);

  const isValidData = (productGroup) => {
    // Define error messages for basic fields
    const errorMessages = {
      productGroupCode: 'Product Group Code is required',
      productGroupName: 'Product Group Name is required',
      catalogueNo: 'Catalogue No is required',
      salesDepartment: 'Sales Department is required',
      mainProductGroup: 'Main Product Group is required',
      powerBiMainProductGroup: 'Power BI Main Product Group is required',
    };

    // Perform basic validation and accumulate errors
    const validationErrors = {};
    const isValid = validateForm(
      productGroup,
      (errors) => {
        Object.assign(validationErrors, errors);
      },
      errorMessages,
    );

    // Additional checks for nested fields
    if (!productGroup.salesDepartment?.id) {
      validationErrors.salesDepartment = errorMessages.salesDepartment;
    }
    if (!productGroup.mainProductGroup?.id) {
      validationErrors.mainProductGroup = errorMessages.mainProductGroup;
    }
    if (!productGroup.powerBiMainProductGroup?.id) {
      validationErrors.powerBiMainProductGroup =
        errorMessages.powerBiMainProductGroup;
    }

    // Set errors and stop loading if any errors exist
    if (Object.keys(validationErrors).length > 0) {
      setError(validationErrors);
      stopLoading();
      return 'error';
    }

    // If all validations pass
    return 'success'; // or true, depending on your use case
  };

  const getMainProductGroup = async () => {
    try {
      const response = await axiosInstance.get(
        `/SmartProAPI/api/main-product-group`,
      );
      setMainProductGroupResponse(response.data);
    } catch (error) {
      handleApiError('error', error.response?.data, error?.response?.status);
    }
  };

  const createMainProductGroup = async (query) => {
    try {
      const response = await axiosInstance.post(
        `/SmartProAPI/api/main-product-group`,
        { mainProductGroupName: query },
      );
      getMainProductGroup();
      setProductGroup((prev) => ({
        ...prev,
        mainProductGroup: {
          id: response.data.id,
          mainProductGroupName: response.data.mainProductGroupName,
        },
      }));
      showAlert('success', 'Main Product Group created successfully');
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
    }
  };

  const getPowerBiMainProductGroup = async () => {
    try {
      const response = await axiosInstance.get(
        `/SmartProAPI/api/power-bi-main-product-group`,
      );
      setPowerBiMainProductGroupResponse(response.data || []);
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
    }
  };

  const createPowerBiMainProductGroup = async (query) => {
    try {
      const response = await axiosInstance.post(
        `/SmartProAPI/api/power-bi-main-product-group`,
        { powerBiMainProductGroupName: query },
      );
      getPowerBiMainProductGroup();
      setProductGroup((prev) => ({
        ...prev,
        powerBiMainProductGroup: {
          id: response.data.id,
          powerBiMainProductGroupName:
            response.data.powerBiMainProductGroupName,
        },
      }));
      showAlert('success', 'Power BI Main Product Group created successfully');
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
    }
  };

  const getSalesDepartment = async () => {
    startLoading();
    try {
      const response = await axiosInstance.get(
        `/SmartProAPI/api/sales-department`,
      );

      setSalesDepartmentResponse(response.data);
    } catch (error) {
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const createNewSalesDepartment = async (salesDepartment) => {
    try {
      const response = await axiosInstance.post(
        `/SmartProAPI/api/sales-department`,
        { salesDepartmentName: salesDepartment },
      );
      getSalesDepartment();
      setProductGroup((prev) => ({
        ...prev,
        salesDepartment: {
          id: response.data.id,
          salesDepartmentName: response.data.salesDepartmentName,
        },
      }));
      showAlert('success', 'Sales Department created successfully');
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
    }
  };

  const getProductGroupByPage = async (
    page = currentPage,
    size = pageSize,
    search = searchQuery,
    sortField = sortBy, // Renamed parameter to avoid shadowing
    sortDirection = sortDir, // Renamed parameter to avoid shadowing
  ) => {
    startLoading();
    try {
      const response = await axiosInstance.get(
        `/SmartProAPI/api/product-group?page=${page}&size=${size}&searchTerm=${search}&sortBy=${sortField}&sortDir=${sortDirection}`,
      );
      setResponse(
        response?.data?._embedded?.productGroupList || ['No Data Found'],
      );
      // if (response.status === 204) {
      //   setTotalElements(0);
      //   return ['No Data Found'];
      // }
      setTotalElements(response?.data?.page?.totalElements || 0);
    } catch (error) {
      setTotalElements(0);

      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };
  const getProductGroup = async () => {
    startLoading();
    try {
      const response = await axiosInstance.get(
        `/SmartProAPI/api/product-group?page=&size=&searchTerm=${searchQuery}`,
      );
      return response?.data?._embedded?.productGroupList;
    } catch (error) {
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const createProductGroup = async (productGroup, e, closeModal) => {
    e.preventDefault();
    const checkValidation = isValidData(productGroup);
    if (checkValidation === 'error') return;
    startLoading();
    try {
      const response = await axiosInstance.post(
        `/SmartProAPI/api/product-group`,
        productGroup,
      );
      showAlert('success', 'Product Group created successfully');
      getProductGroupByPage();
      closeModal();
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const updateProductGroup = async (productGroup, id, e, closeModal) => {
    e.preventDefault();
    const checkValidation = isValidData(productGroup);
    if (checkValidation === 'error') return;
    startLoading();
    try {
      const response = await axiosInstance.put(
        `/SmartProAPI/api/product-group/${id}`,
        productGroup,
      );
      getProductGroupByPage();
      showAlert('success', 'Product Group updated successfully');
      closeModal();
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const deleteProductGroup = async (id) => {
    const result = await ConfirmAlert({
      confirmBtnText: 'Delete Product Group',
    });
    if (!result.isConfirmed) {
      stopLoading();
      handleApiError(
        'error',
        'Product Group cancelled',
        error?.response?.status,
      );
      return;
    }
    startLoading();
    axiosInstance
      .delete(`/SmartProAPI/api/product-group/${id}`)
      .then(() => {
        getProductGroupByPage();
        showAlert('success', 'Product Group deleted successfully');
      })
      .catch((error) => {
        handleApiError('error', error.response.data, error?.response?.status);
      });
    stopLoading();
  };

  const clearFormData = () => {
    setProductGroup({ ...initialState });
    setError({});
  };

  const handleSearchQueryChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(0); // Reset to 0th page whenever search query changes
  };
  return (
    <ProductGroupContext.Provider
      value={{
        isLoading,
        startLoading,
        stopLoading,
        productGroup,
        setProductGroup,
        initialState,
        error,
        setError,
        getMainProductGroup,
        createMainProductGroup,
        getPowerBiMainProductGroup,
        createPowerBiMainProductGroup,
        getSalesDepartment,
        getProductGroup,
        createProductGroup,
        updateProductGroup,
        deleteProductGroup,
        clearFormData,
        productGroupResponse,
        salesDepartmentResponse,
        powerBiMainProductGroupResponse,
        mainProductGroupResponse,
        createNewSalesDepartment,
        searchQuery,
        setCurrentPage,
        setSearchQuery: handleSearchQueryChange,
        totalElements,
        setTotalElements,
        currentPage,
        pageSize,
        setProductGroupResponse,
        getProductGroupByPage,
        sortBy,
        setSortBy,
        sortDir,
        setSortDir,
        response,
      }}>
      {children}
    </ProductGroupContext.Provider>
  );
};

export default ProductGroupContextProvider;
export const useProductGroup = () => useContext(ProductGroupContext);
